import type { SdkApiType } from "../types/sdkApi";
import { SDK_API_READY_EVENT } from "../types/hostBridge";
import { wrapSdkApiWithPerf } from "@eweadn-qiankun/shared-utils";

let cached: SdkApiType | null = null;
let loading: Promise<SdkApiType> | null = null;

// --- demo mode ---
let demoCached: SdkApiType | null = null;
let demoLoading: Promise<SdkApiType> | null = null;

function isDemoMode(): boolean {
  try {
    return localStorage.getItem("isDemoMode") === "true";
  } catch {
    return false;
  }
}

function readWindowSdkApi(): SdkApiType | null {
  if (typeof window === "undefined") return null;
  return (window as Window & { sdkApi?: SdkApiType }).sdkApi ?? null;
}

function mountSdkApiToWindow(sdkApi: SdkApiType): void {
  if (typeof window === "undefined") return;
  const w = window as Window & { sdkApi?: SdkApiType };
  w.sdkApi = sdkApi;
  window.dispatchEvent(
    new CustomEvent(SDK_API_READY_EVENT, { detail: { sdkApi } })
  );
}

function prepareSdkApiForRuntime(sdkApi: SdkApiType): SdkApiType {
  try {
    return wrapSdkApiWithPerf(sdkApi);
  } catch {
    return sdkApi;
  }
}

/** 同步读取已加载的 sdkApi；shell 首屏阶段可能为 null */
export function getSdkApiSync(): SdkApiType | null {
  if (isDemoMode()) return demoCached;
  return cached ?? readWindowSdkApi();
}

async function preloadCurrentDeviceProviders(): Promise<void> {
  const [
    { ensureCurrentKeyboardProviderLoaded },
    { ensureCurrentMouseProviderLoaded },
  ] = await Promise.all([
    import("./keyboard/dataConversion"),
    import("./mouse/dataConversion"),
  ]);
  await Promise.all([
    ensureCurrentKeyboardProviderLoaded(),
    ensureCurrentMouseProviderLoaded(),
  ]);
}

/** 加载演示模式 mock SDK（单例） */
async function getDemoSdkApi(): Promise<SdkApiType> {
  // 只用 demoCached，不读 window.sdkApi（可能残留真实 SDK）
  if (demoCached) return demoCached;

  if (!demoLoading) {
    demoLoading = import(/* webpackChunkName: "demo-sdk" */ "./demo").then(
      (mod) => {
        demoCached = prepareSdkApiForRuntime(mod.default as SdkApiType);
        mountSdkApiToWindow(demoCached);
        console.log("[MainApp][sdkApi] demo SDK loaded", {
          mouseKeys: Object.keys(demoCached.mouse || {}),
          keyboardKeys: Object.keys(demoCached.keyboard || {}),
          earphoneKeys: Object.keys(demoCached.earphone || {}),
        });
        return demoCached as SdkApiType;
      }
    );
  }
  return demoLoading;
}

/** 异步加载 sdkApi 壳层 + 当前设备 provider（单例） */
export function getSdkApi(): Promise<SdkApiType> {
  // 演示模式：加载 demo SDK
  if (isDemoMode()) {
    return getDemoSdkApi();
  }

  const existing = getSdkApiSync();
  if (existing) {
    cached = existing;
    return Promise.resolve(existing);
  }

  if (!loading) {
    loading = import(/* webpackChunkName: "sdk-api" */ "./index").then(
      async (mod) => {
        cached = prepareSdkApiForRuntime(mod.default);
        mountSdkApiToWindow(cached);
        await preloadCurrentDeviceProviders();
        // eslint-disable-next-line no-console
        console.log("[MainApp][sdkApi] async loaded", {
          mouseKeys: Object.keys(cached.mouse || {}),
          keyboardKeys: Object.keys(cached.keyboard || {}),
        });
        return cached;
      }
    );
  }

  return loading;
}

export function assertSdkApiReady(): SdkApiType {
  const api = getSdkApiSync();
  if (!api) {
    throw new Error("[sdkApi] 尚未加载，请先 await getSdkApi()");
  }
  return api;
}

/** 退出演示模式时调用：清除 demo SDK 缓存，恢复真实 SDK 到 window */
export function clearDemoSdkState(): void {
  demoCached = null;
  demoLoading = null;
  if (typeof window === "undefined") return;
  const w = window as Window & { sdkApi?: SdkApiType };
  if (cached) {
    w.sdkApi = cached;
    window.dispatchEvent(
      new CustomEvent(SDK_API_READY_EVENT, { detail: { sdkApi: cached } })
    );
  } else {
    delete w.sdkApi;
  }
}

/** 连接 / 子应用 mount 前调用，保证 sdkApi 与当前 provider 就绪 */
export async function ensureConnectSdkReady(): Promise<SdkApiType> {
  return getSdkApi();
}
